/**
 * The "there is nothing here yet" panel.
 *
 * Lives in `shared/core-ui` because `features/files` and
 * `features/org-connections` both render one inside the SAME dialog — the
 * Who-has-access Links and Outsiders views — and neither may import the other.
 * A copy per feature is how two empty states in one screen end up looking like
 * two different products.
 *
 * WHY IT EXISTS AT ALL. The states it replaced were a single line of muted
 * text, left-aligned against the top of an otherwise blank pane: "No
 * share-links match this filter." Two things were wrong with that beyond the
 * plainness.
 *
 *   · It read as a FAILURE, not as a starting point. Nothing is broken when a
 *     folder has never been shared; that is the normal state of most folders,
 *     and the screen should say what the view is for rather than report a
 *     count of zero.
 *   · "match this filter" was often a lie. On a phone the audience filters are
 *     pinned to "all" — there is no filter to match — so the one sentence on
 *     screen explained the emptiness with a control the reader could not see
 *     and had not touched. Hence `filtered`: the copy has to know which of the
 *     two situations it is in, because they call for opposite next steps
 *     (clear the filter, versus create the first one).
 */
import type { ReactNode } from 'react'
import type { GlyphIcon } from './PDAppIcon'

export function EmptyState({ icon: Icon, title, body, action, compact = false }: {
  /** `GlyphIcon`, not `LucideIcon` — the union covers both lucide's icons and
   *  the app's own (`PDAppIcon`), which is a plain function component and does
   *  not satisfy lucide's forwardRef type. Typing this narrowly made the one
   *  in-house icon the only one this component could not display. */
  icon: GlyphIcon
  /** One short line. What this view holds, not "no results". */
  title: string
  /** One sentence explaining what would appear here, or how to fix a filter. */
  body: string
  /** The primary action, when there is one worth offering from here. */
  action?: ReactNode
  /** Tighter padding, for a pane that is already short. */
  compact?: boolean
}) {
  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center gap-3 text-center ${
        compact ? 'px-5 py-8' : 'px-6 py-12'
      }`}
    >
      <span
        aria-hidden
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full themed-bg-tertiary"
      >
        <Icon size={22} className="themed-text-muted" />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium themed-text">{title}</p>
        {/* `max-w-[38ch]` rather than a pixel width: the measure should follow
            the TEXT, so a long sentence wraps at a readable line length on a
            wide pane instead of running the full width of it. */}
        <p className="mx-auto max-w-[38ch] text-[13px] leading-relaxed themed-text-muted">{body}</p>
      </div>
      {action && <div className="pt-1 w-full max-w-[18rem]">{action}</div>}
    </div>
  )
}
